
import {
    hasPrimaryModifier
} from "diagram-js/lib/util/Mouse";

import {
    is,
    isAny
} from "bpmn-js/lib/util/ModelUtil";
import * as _ from "lodash";
import { getElementType } from "../ElementFactory";
import { ElementType } from "../IProcessData";

import { FrendsRuleProvider } from "../FrendsRules/FrendsRuleProvider";

import Canvas from "diagram-js/lib/core/Canvas";
import ElementFactory from "diagram-js/lib/core/ElementFactory";
import EventBus from "diagram-js/lib/core/EventBus";
import Connect from "diagram-js/lib/features/connect/Connect";
import ContextPad from "diagram-js/lib/features/context-pad/ContextPad";
import Create from "diagram-js/lib/features/create/Create";
import Modeling from "diagram-js/lib/features/modeling/Modeling";
import Rules from "diagram-js/lib/features/rules/Rules";
import { TranslateFn } from "diagram-js/lib/i18n/translate/translate";
import { Base } from "diagram-js/lib/model";
import { Injector } from "didi";
import store from "../../index";
import { Triggers } from "../ITriggerData";
import colorpalette from "../../../shared/icons/colorpalette.svg"

/**
 * A provider for BPMN 2.0 elements context pad
 * Mostly copy of bpmn-js/lib/features/context-pad/ContextPadProvider.js version 6.3.0
 * Some unneeded things removed and added some of our own integrations
 * This API should not break but we might miss some possible features if they update.
 */
export default function ContextPadProvider(
    config: any, injector: Injector, eventBus: EventBus,
    contextPad: ContextPad, modeling: Modeling, elementFactory: ElementFactory,
    connect: Connect, create: Create, popupMenu: any,
    canvas: Canvas, rules: Rules, translate: TranslateFn) {

    config = config || {};

    contextPad.registerProvider(this);

    this._contextPad = contextPad;

    this._modeling = modeling;

    this._elementFactory = elementFactory;
    this._connect = connect;
    this._create = create;
    this._popupMenu = popupMenu;
    this._canvas = canvas;
    this._rules = rules;
    this._translate = translate;

    if (config.autoPlace !== false) {
        this._autoPlace = injector.get("autoPlace", false);
    }

    eventBus.on("create.end", 250, function (event) {
        const shape = event.context.shape;

        if (!hasPrimaryModifier(event)) {
            return;
        }

        const entries = contextPad.getEntries(shape);

        if (entries.replace.action?.click) {
            entries.replace.action.click(event, shape);
        }
    });
}

ContextPadProvider.$inject = [
    "config.contextPad",
    "injector",
    "eventBus",
    "contextPad",
    "modeling",
    "elementFactory",
    "connect",
    "create",
    "popupMenu",
    "canvas",
    "rules",
    "translate"
];

ContextPadProvider.prototype.getContextPadEntries = function (element: any) {

    const contextPad = this._contextPad,
        modeling = this._modeling,
        elementFactory = this._elementFactory,
        connect = this._connect,
        create = this._create,
        popupMenu = this._popupMenu,
        canvas = this._canvas,
        rules = this._rules,
        autoPlace = this._autoPlace,
        translate = this._translate;

    const actions: { [key: string]: string } = {};

    if (element.type === "label") {
        return actions;
    }

    const businessObject = element.businessObject;

    function startConnect(event: any, element: any) {
        connect.start(event, element);
    }

    function removeElement(e: any) {
        modeling.removeElements([element]);
    }

    function getReplaceMenuPosition(element: any) {

        const Y_OFFSET = 5;

        const diagramContainer = canvas.getContainer(),
            pad = contextPad.getPad(element).html;

        const diagramRect = diagramContainer.getBoundingClientRect(),
            padRect = pad.getBoundingClientRect();

        const top = padRect.top - diagramRect.top;
        const left = padRect.left - diagramRect.left;

        const pos = {
            x: left,
            y: top + padRect.height + Y_OFFSET
        };

        return pos;
    }

    function appendAction(type: any, className: any, title?: any, options?: any) {

        // Validate if the type can be appended
        const tempElement = elementFactory.createShape(Object.assign({ type: type }));
        const elementType = getElementType(element.type);
        // if the element is of a type we don't support, remove creation elements. Otherwise check if our ruleset allows the creation:
        if ((elementType == null || elementType === ElementType.SequenceFlow) ||
            FrendsRuleProvider.canConnect(element, tempElement as BpmnJsElement, null) != null) {
            return null;
        }

        if (typeof title !== "string") {
            options = title;
            title = translate("Append {type}", { type: type.replace(/^bpmn:/, "") });
        }

        function appendStart(event: any, element: any) {

            const shape = elementFactory.createShape(Object.assign({ type: type }, options));
            create.start(event, shape, {
                source: element
            });
        }

        const append = autoPlace ? function (event: any, element: any) {
            const shape = elementFactory.createShape(Object.assign({ type: type }, options));

            autoPlace.append(element, shape);
        } : appendStart;

        return {
            group: "model",
            className: className,
            title: title,
            action: {
                dragstart: appendStart,
                click: append
            }
        };
    }

    if (is(businessObject, "bpmn:FlowNode")) {
        if (!isAny(businessObject as unknown as Base, ["bpmn:EndEvent", "bpmn:IntermediateThrowEvent", "bpmn:IntermediateCatchEvent"])) {
            Object.assign(actions, {
                "append.end-event": appendAction(
                    "bpmn:EndEvent",
                    "bpmn-icon-end-event-none",
                    "Append return"
                ),
                "append.gateway": appendAction(
                    "bpmn:ExclusiveGateway",
                    "bpmn-icon-gateway-xor",
                    "Append Exclusive Descision"
                )
            });
        }

        if (!isAny(businessObject as unknown as Base, ["bpmn:EndEvent", "bpmn:IntermediateThrowEvent"])) {
            Object.assign(actions, {
                "append.append-task": appendAction(
                    "bpmn:Task",
                    "bpmn-icon-task",
                    "Append Task"
                ),
                "append.create-callactivity": appendAction(
                    "bpmn:CallActivity",
                    "bpmn-icon-call-activity",
                    "Append Call Subprocess"
                ),
                "append.script-task": appendAction(
                    "bpmn:ScriptTask",
                    "bpmn-icon-script",
                    "Append Code",
                    { height: 30, width: 30 }
                ),
                "connect": {
                    group: "connect",
                    className: "bpmn-icon-connection-multi",
                    title: "Connect",
                    action: {
                        click: startConnect,
                        dragstart: startConnect
                    }
                }
            });
        }
    }

    const frendsElement = store.getState().elements[element.id];
    if (is(businessObject, "bpmn:StartEvent")
        && !popupMenu.isEmpty(element, "bpmn-replace")
        && is(businessObject.$parent, "bpmn:Process")
        && frendsElement?.selectedTypeId !== Triggers.HttpApiTrigger.triggerType) {
        // Replace menu entry
        Object.assign(actions, {
            replace: {
                group: "edit",
                className: "bpmn-icon-screw-wrench",
                title: translate("Change type"),
                action: {
                    click: function (event: any, element: any) {

                        const position = Object.assign(getReplaceMenuPosition(element), {
                            cursor: { x: event.x, y: event.y }
                        });

                        popupMenu.open(element, "bpmn-replace", position);
                    }
                }
            }
        });
    }

    if (isAny(businessObject, [
        "bpmn:DataObjectReference",
        "bpmn:DataStoreReference"
    ])) {
        Object.assign(actions, {
            connect: {
                group: "connect",
                className: "bpmn-icon-connection-multi",
                title: "Connect",
                action: {
                    click: startConnect,
                    dragstart: startConnect
                }
            }
        });
    }
    if (isAny(businessObject, [
        "bpmn:FlowNode",
        "bpmn:InteractionNode",
        "bpmn:DataObjectReference",
        "bpmn:DataStoreReference"
    ])) {

        Object.assign(actions, {
            "append.text-annotation": appendAction("bpmn:TextAnnotation", "bpmn-icon-text-annotation", "Append Text Annotation"),
            "set-color": {
                group: "edit",
                className: "bpmn-icon-color",
                title: "Set Color",
                imageUrl: colorpalette,
                action: {
                    click: function (event: any, elem: any) {
                        // close any existing popup
                        popupMenu.close();

                        // get start popup draw start position
                        const position = Object.assign(getReplaceMenuPosition(element), {
                            cursor: { x: event.x, y: event.y }
                        });

                        // open color picker submenu popup
                        popupMenu.open(elem, "color-picker", position);
                    }
                }
            }
        });
    }

    // delete element entry, only show if allowed by rules
    let deleteAllowed = rules.allowed("elements.delete", { elements: [element] });

    if (_.isArray(deleteAllowed)) {
        // was the element returned as a deletion candidate?
        deleteAllowed = deleteAllowed[0] === element;
    }

    if (deleteAllowed) {
        Object.assign(actions, {
            delete: {
                group: "edit",
                className: "bpmn-icon-trash",
                title: translate("Remove"),
                action: {
                    click: removeElement
                }
            }
        });
    }

    // if a rule desides that a element should not have an action the value of the key is just empty.
    Object.keys(actions).forEach((key) => (actions[key] == null) && delete actions[key]);
    return actions;
};
